import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, LogOut, Users, LayoutDashboard, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getAdminWriters, deleteWriter, verifyAdmin, adminLogout } from '@/lib/api';
import { toast } from 'sonner';

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

export default function AdminWriters() {
  const navigate = useNavigate();
  const [writers, setWriters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const fetchWriters = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAdminWriters();
      setWriters(data);
    } catch (error) {
      toast.error('Failed to load writers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (!user) {
        navigate('/admin');
        return;
      }
      if (user.role && user.role !== 'admin') {
        navigate('/admin/dashboard');
        return;
      }
      fetchWriters();
    };
    checkAuth();
  }, [navigate, fetchWriters]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteWriter(deleteId);
      toast.success('Writer deleted');
      fetchWriters();
    } catch (error) {
      toast.error('Failed to delete writer');
    } finally {
      setDeleteId(null);
    }
  };

  const handleLogout = () => {
    adminLogout();
    toast.success('Logged out');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-writers">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-surface border-r border-zinc-800 p-6 hidden lg:block">
        <div className="mb-8">
          <span className="font-heading text-2xl font-black text-[#CCFF00] tracking-tighter">BORMULA</span>
          <p className="font-mono text-xs text-text-secondary uppercase mt-1">Admin Panel</p>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span className="font-body font-medium">Dashboard</span>
          </Link>
          <Link to="/admin/writers" className="flex items-center gap-3 px-4 py-3 bg-[#CCFF00]/10 text-[#CCFF00] rounded-none">
            <Users className="w-5 h-5" />
            <span className="font-body font-medium">Writers</span>
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6 space-y-4">
          <a href="/" target="_blank" className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-white transition-colors">
            <Eye className="w-5 h-5" />
            <span className="font-body">View Site</span>
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-red-400 transition-colors w-full"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-body">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-8">
          <span className="font-heading text-2xl font-black text-[#CCFF00]">BORMULA</span>
          <button onClick={handleLogout} className="text-text-secondary">
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="font-heading text-3xl font-black text-white uppercase">Writers</h1>
          <Link to="/admin/writer/new">
            <Button
              className="bg-[#CCFF00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide"
              data-testid="new-writer-button"
            >
              <Plus className="w-4 h-4 mr-2" /> New Writer
            </Button>
          </Link>
        </div>

        {/* Writers Grid */}
        {writers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {writers.map((writer) => (
              <div
                key={writer.id}
                className="bg-surface border border-zinc-800 p-6 hover:border-[#CCFF00]/20 transition-colors"
                data-testid={`writer-card-${writer.id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  {writer.image_url ? (
                    <img src={writer.image_url} alt={writer.name} className="w-14 h-14 rounded-full object-cover border border-zinc-700 flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/20 flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-lg font-black text-[#CCFF00]">{getInitials(writer.name)}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-black text-white uppercase truncate">{writer.name}</h3>
                    <p className="font-mono text-xs text-text-secondary">Added {formatDate(writer.created_at)}</p>
                  </div>
                </div>

                {writer.bio && (
                  <p className="font-body text-sm text-text-secondary line-clamp-2 mb-4">{writer.bio}</p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                  <Link
                    to={`/writer/${writer.slug}`}
                    target="_blank"
                    className="text-text-secondary hover:text-[#CCFF00] transition-colors text-xs font-bold uppercase tracking-wider"
                  >
                    View Profile
                  </Link>
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/writer/${writer.id}`}>
                      <Button variant="ghost" size="sm" className="text-text-secondary hover:text-white hover:bg-white/10" data-testid={`edit-writer-${writer.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-text-secondary hover:text-red-400 hover:bg-red-500/10"
                      onClick={() => setDeleteId(writer.id)}
                      data-testid={`delete-writer-${writer.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-surface border border-zinc-800 text-center py-16">
            <Users className="w-12 h-12 text-text-secondary mx-auto mb-4" />
            <p className="font-heading text-xl text-white mb-2">No writers yet</p>
            <p className="font-body text-text-secondary mb-6">Add your first writer to get started</p>
            <Link to="/admin/writer/new">
              <Button className="bg-[#CCFF00] text-black hover:bg-[#b3e600]">
                <Plus className="w-4 h-4 mr-2" /> Add Writer
              </Button>
            </Link>
          </div>
        )}
      </main>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-surface border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Writer</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              Are you sure? This will remove the writer and unlink them from all their articles.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-writer">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
